import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import type { RegistrationStammdatenPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';

export const icsrmFollowUpStammdatenPlugin: RegistrationStammdatenPlugin = {
  id: 'registration::stammdaten::icsrm-follow-up',
  type: 'registration::stammdaten',
  disableCustomFormfields: true,
  createStammdaten: async ({ userID, institutionID, createInDB } = {}) => {
    const stammdaten = new IcsrmFollowUpStammdaten({ userID, institutionID });
    if (createInDB) await stammdaten.create();
    return stammdaten;
  },
  getFields: () => {
    return Object.keys(IcsrmFollowUpStammdaten.fields).filter(
      it => !['_id', '_class', 'userID', 'institutionID'].includes(it),
    );
  },
  classType: () => IcsrmFollowUpStammdaten,
};

// University enum for "Derzeitige Hochschule" field
export enum IcsrmFollowUpUniEnum {
  ICSRM_FRA = 'ICSRM_FRA',
  ICSRM_GU = 'ICSRM_GU',
  ICSRM_HDA = 'ICSRM_HDA',
  ICSRM_HSRM = 'ICSRM_HSRM',
  ICSRM_ICSRM = 'ICSRM_ICSRM',
  ICSRM_JGU = 'ICSRM_JGU',
  ICSRM_ANDERE = 'ICSRM_ANDERE',
}
registerEnum({ enum: IcsrmFollowUpUniEnum, name: 'IcsrmFollowUpUniEnum' });

@Entity()
export class IcsrmFollowUpStammdaten extends UserStammdaten {
  static override _class = 'IcsrmFollowUpStammdaten';

  override getTranslationNamespace() {
    return 'icsrm-follow-up';
  }

  // Zuwanderungsgeschichte
  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: value => value ?? false })
  isImmigrant: Maybe<boolean>;

  // Derzeitige Hochschule
  @validate(nonOptional())
  @Field(type => IcsrmFollowUpUniEnum)
  currentUniversity: Maybe<IcsrmFollowUpUniEnum>;

  // Checkbox: "Ich bin einverstanden, dass der ICS-RM mich kontaktiert"
  @validate(({ value }) => {
    if (value !== true) {
      throw new ValidationError('Contact consent is required!', {
        key: 'contactConsent',
        translationKey: 'validation_contact_consent_required',
      });
    }
  })
  @Field(type => Boolean, { default: value => value ?? false })
  contactConsent: Maybe<boolean>;
}
